import { computed, onMounted, ref, watch } from 'vue';
import useProductImagesCtaStore from '~/stores/useProductImagesCtaStore';
import { useElementSize } from '@vueuse/core';
import useExperiments from '~/stores/useExperimentsStore';
import useKillswitches from '~/modules/featureControl/composables/useKillswitches';
import { useBrand } from '~/composables/useBrand';

/**
 * Import each CTA item composable which have their own module specific
 * logic needed to be triggered during page load
 */
import useVtoCta from '~/composables/useProductImagesCta/useVtoCta';
import { useWebAr } from '~/composables/useProductImagesCta/useWebAr';
import usePartOfCollection from '~/composables/useProductImagesCta/usePartOfCollection';
import useOutfittingCta from '~/composables/useProductImagesCta/useOutfittingCta';
import useValuePriceCta from './useValuePriceCta';

/**
 *
 * @param {string} [productId = '']
 * @param {import('vue').ComputedRef<html>} [productCtaContainer = null]
 * @return {Object}
 */
export default function useProductImagesCta(productId = '', productCtaContainer = null) {
  const productImagesCtaStore = useProductImagesCtaStore();

  const {
    experimentFlags: { shopTheLookEnabled, isImageIndicatorStylingEnabled, isImageARIndicatorEnabled },
  } = useExperiments();
  const { killswitches } = useKillswitches();
  const { isMcom } = useBrand();
  const shouldShowOutfittingCta = !shopTheLookEnabled;
  const showValueCta = isMcom && killswitches.isPDPEnhancementBEnabled;

  const { init: outfittingCtaInit } = useOutfittingCta();
  const { init: partOfCollectionCtaInit } = usePartOfCollection();
  const { init: useVtoCtaInit } = useVtoCta();
  const { init: valuePriceCtaInit } = useValuePriceCta();

  const isReady = ref(false);
  const sortedList = computed(() => productImagesCtaStore.sortedList);

  /**
   * Set CTA items order
   * @param {string[]} ids
   */
  const setItemsOrder = async (ids) => {
    // Filter ids leaving out any non string
    const filteredIds = ids.filter((item) => typeof item === 'string');
    await productImagesCtaStore.setItemsOrder(filteredIds);
  };

  const redesignedIndicatorsInit = () =>
    Promise.all([
      setItemsOrder(['valuePrice', 'partOfCollection']),
      partOfCollectionCtaInit(),
      showValueCta ? valuePriceCtaInit() : Promise.resolve(),
    ]).then(() => {
      isReady.value = true;
    });

  const init = () =>
    Promise.all([
      setItemsOrder(['valuePrice', 'partOfCollection', 'marxent', 'vto', 'outfitting']),
      useVtoCtaInit(),
      shouldShowOutfittingCta ? outfittingCtaInit() : Promise.resolve(),
      partOfCollectionCtaInit(),
      !isImageIndicatorStylingEnabled && !isImageARIndicatorEnabled && useWebAr(sortedList),
      showValueCta ? valuePriceCtaInit() : Promise.resolve(),
    ]).then(() => {
      isReady.value = true;
    });

  onMounted(isImageIndicatorStylingEnabled ? redesignedIndicatorsInit : init);

  if (productId && productCtaContainer) {
    // create new cta dimensions entry
    productImagesCtaStore.ensureDimensionsEntry(productId);

    // get ct container dimensions
    const { height, width } = useElementSize(productCtaContainer);

    watch(
      [() => height.value, () => width.value],
      ([newHeight, newWidth]) => {
        if (newHeight) {
          productImagesCtaStore.setItemsHeight({ key: productId, height: newHeight });
        }
        if (newWidth) {
          productImagesCtaStore.setItemsHeight({ key: productId, width: newWidth });
        }
      },
      { immediate: true },
    );
  }

  return {
    init,
    isReady,
    sortedList,
  };
}
